import React, { useState, useEffect } from 'react';
import { Card, Button, Alert, Typography, Space, Tag, Spin, Steps, Result } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, ReloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import WaterLinkService from '../../services/WaterLinkService';
import ElectronWaterLinkService from '../../services/ElectronWaterLinkService';

const { Title, Text } = Typography;

const SpintouchTest = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({});
  const [errors, setErrors] = useState({});

  const steps = [
    {
      title: 'Détection de l\'environnement',
      description: 'Vérifier si l\'application s\'exécute dans Electron ou un navigateur'
    },
    {
      title: 'Test du service WaterLink',
      description: 'Vérifier la connectivité avec le service WaterLink Connect'
    },
    {
      title: 'Test de l\'appareil',
      description: 'Vérifier la détection et l\'état de l\'appareil Spintouch'
    },
    {
      title: 'Test des options',
      description: 'Récupérer les options de test disponibles'
    },
    {
      title: 'Test complet',
      description: 'Effectuer un test d\'eau complet'
    }
  ];

  const runAllTests = async () => {
    setLoading(true);
    setCurrentStep(0);
    setResults({});
    setErrors({});

    try {
      // Étape 1: Détection de l'environnement
      setCurrentStep(1);
      const isElectron = ElectronWaterLinkService.isElectronApp();
      setResults(prev => ({ ...prev, environment: { isElectron, mode: isElectron ? 'Electron' : 'Navigateur' } }));
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Étape 2: Test du service
      setCurrentStep(2);
      const service = isElectron ? ElectronWaterLinkService : WaterLinkService;
      const serviceInfo = await service.checkService();
      setResults(prev => ({ ...prev, service: serviceInfo }));
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Étape 3: Test de l'appareil
      setCurrentStep(3);
      const deviceStatus = await service.getStatus();
      setResults(prev => ({ ...prev, device: deviceStatus }));
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Étape 4: Test des options
      setCurrentStep(4);
      if (deviceStatus.CanDoWatertest) {
        const options = await service.getOptions();
        setResults(prev => ({ ...prev, options }));
      } else {
        setResults(prev => ({ ...prev, options: { message: 'Appareil ne peut pas effectuer de tests' } }));
      }
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Étape 5: Test complet (simulation)
      setCurrentStep(5);
      setResults(prev => ({ ...prev, test: { message: 'Test simulé avec succès' } }));

    } catch (error) {
      setErrors(prev => ({ ...prev, [currentStep]: error.message }));
    } finally {
      setLoading(false);
    }
  };

  const getStepStatus = (stepIndex) => {
    if (stepIndex < currentStep) {
      return 'finish';
    } else if (stepIndex === currentStep) {
      return 'process';
    } else {
      return 'wait';
    }
  };

  const renderResults = () => {
    return (
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {results.environment && (
          <Card size="small" title="Environnement">
            <Space>
            
              <Text>{results.environment.isElectron ? 'Mode Electron détecté' : 'Mode Navigateur détecté'}</Text>
            </Space>
          </Card>
        )}

        {results.service && (
          <Card size="small" title="Service WaterLink">
            <Space direction="vertical" size="small">
              <div>
                <Text strong>Version: </Text>
                <Text>{results.service.version}</Text>
              </div>
              <div>
                <Text strong>Documentation: </Text>
                <Text>{results.service.documentation}</Text>
              </div>
            </Space>
          </Card>
        )}

        {results.device && (
          <Card size="small" title="Appareil">
            <Space direction="vertical" size="small">
              <div>
                <Text strong>Nom: </Text>
                <Text>{results.device.Name || 'Non connecté'}</Text>
              </div>
              <div>
                <Text strong>Statut: </Text>
                <Tag color={results.device.Status === 5 ? 'green' : 'orange'}>
                  {WaterLinkService.getStatusText(results.device.Status)}
                </Tag>
              </div>
              <div>
                <Text strong>Firmware: </Text>
                <Text>{results.device.CurrentFirmwareString || 'N/A'}</Text>
              </div>
            </Space>
          </Card>
        )}

        {results.options && (
          <Card size="small" title="Options de test">
            {results.options.message ? (
              <Text type="secondary">{results.options.message}</Text>
            ) : (
              <Space direction="vertical" size="small">
                <div>
                  <Text strong>Types d'échantillons: </Text>
                  <Text>{results.options.SampleTypes?.length || 0}</Text>
                </div>
                <div>
                  <Text strong>Types de réactifs: </Text>
                  <Text>{results.options.ReagentTypes?.length || 0}</Text>
                </div>
              </Space>
            )}
          </Card>
        )}

        {results.test && (
          <Card size="small" title="Test complet">
            <Text type="success">{results.test.message}</Text>
          </Card>
        )}
      </Space>
    );
  };

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Test de fonctionnement Spintouch</Title>
      
      <Card>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Button 
              type="primary" 
              icon={<ReloadOutlined />} 
              onClick={runAllTests}
              loading={loading}
              size="large"
            >
              Lancer les tests
            </Button>
          </div>

          <Steps
            current={currentStep}
            items={steps.map((step, index) => ({
              ...step,
              status: getStepStatus(index)
            }))}
          />

          {loading && (
            <div style={{ textAlign: 'center' }}>
              <Spin size="large" />
              <div style={{ marginTop: 16 }}>
                <Text>Exécution des tests...</Text>
              </div>
            </div>
          )}

          {Object.keys(errors).length > 0 && (
            <Alert
              message="Erreurs détectées"
              description={
                <Space direction="vertical">
                  {Object.entries(errors).map(([step, error]) => (
                    <div key={step}>
                      <Text strong>Étape {step}: </Text>
                      <Text type="danger">{error}</Text>
                    </div>
                  ))}
                </Space>
              }
              type="error"
              showIcon
            />
          )}

          {!loading && Object.keys(results).length > 0 && (
            <div>
              <Title level={4}>Résultats des tests</Title>
              {renderResults()}
            </div>
          )}

          {!loading && currentStep === 5 && Object.keys(errors).length === 0 && (
            <Result
              status="success"
              title="Tous les tests ont réussi !"
              subTitle="Le système Spintouch est prêt à être utilisé."
            />
          )}
        </Space>
      </Card>
    </div>
  );
};

export default SpintouchTest;

